import { Chip } from "@/components/ui";
import React from "react";
import { isPriorityField } from "@/utils/normalized-model-fields";
import { getDisplayName, isPrefixedField } from "@/utils/fields-mapping";

// Type for chip colors matching our Arc-style Chip component
type ChipColor = "default" | "primary" | "success" | "warning" | "error" | "info";

interface FieldItemProps {
  field: string;
  type?: string; // Type du champ (text, date, integer, etc.) - keyword est affiché comme text
}

const KEY_TAG_MAP = new Map<string, { color: ChipColor; label: string }>([
  ["normalized", { color: "info", label: "doc" }],
  ["extra", { color: "primary", label: "extra" }],
  ["misc", { color: "default", label: "misc" }],
]);

// Map des couleurs pour les types de champs
// Note: "keyword" n'est plus affiché - tous les champs texte sont affichés comme "text"
const TYPE_COLOR_MAP = new Map<string, ChipColor>([
  ["text", "info"],
  ["date", "warning"],
  ["integer", "success"],
  ["long", "success"],
  ["float", "success"],
  ["double", "success"],
  ["boolean", "primary"],
  ["object", "default"],
  ["nested", "primary"],
  ["number", "success"],
]);

// Normalise le type pour l'affichage (keyword → text pour transparence utilisateur)
const normalizeTypeForDisplay = (type: string): string => {
  if (type === "keyword") return "text";
  return type;
};

const FieldItem: React.FC<FieldItemProps> = ({ field, type }) => {
  // Si le champ est au format type$name (ex: "num$likes"), extraire le nom d'affichage
  let displayField = field;
  if (isPrefixedField(field)) {
    displayField = getDisplayName(field);
  }
  
  // Retirer le préfixe "normalized." du champ pour l'affichage
  if (displayField.startsWith("normalized.")) {
    displayField = displayField.substring("normalized.".length);
  }

  const keys = displayField.split(".");

  // Vérifier si c'est un champ prioritaire (normalized) via isPriorityField
  const isPriority = isPriorityField(field);

  // Si un type est fourni, utiliser le type pour la chip
  // Ne plus afficher "doc" pour les champs prioritaires
  // Normaliser le type pour l'affichage (keyword → text)
  let tag;
  if (type) {
    const displayType = normalizeTypeForDisplay(type);
    const typeColor = TYPE_COLOR_MAP.get(displayType) || "default";
    tag = { color: typeColor, label: displayType };
  } else {
    // Déterminer le tag seulement pour extra/misc, pas pour doc
    const hasTag = KEY_TAG_MAP.get(keys[0]);
    // Ne pas afficher de tag pour les champs prioritaires (doc)
    if (!hasTag && isPriority) {
      tag = null; // Pas de chip pour les champs prioritaires sans type
    } else {
      tag = hasTag ?? KEY_TAG_MAP.get("misc");
    }
  }

  const removeKeyword = keys[keys.length - 1] === "keyword";
  // Afficher le nom sans le préfixe "normalized." et sans ".keyword"
  // Si le tag est "doc" (normalized), on a déjà retiré "normalized." donc on affiche tout
  // Si le tag est "extra" ou "misc", on retire le premier segment qui est le namespace
  const name = keys.slice(
    !type && tag && tag.label !== "doc" ? 1 : 0,
    removeKeyword ? -1 : undefined
  );

  return (
    <div className="flex items-center justify-between gap-2 w-full">
      <span>{name.join(".")}</span>
      {tag && (
        <Chip
          size="sm"
          color={tag.color}
          className="py-0.5"
        >
          {tag.label}
        </Chip>
      )}
    </div>
  );
};

export default FieldItem;
